// ----------------------------------------------------------------------- react
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";

// ------------------------------------------------------------------- internos
import { useAuth } from "../auth/AuthContext";
import BotonConfirmar from "../components/funcionales/BotonConfirmar";
import FotoPrivada from "../components/funcionales/FotoPrivada";
import PuertaCerrada from "../components/funcionales/PuertaCerrada";
import {
  borrar,
  choferes as traerChoferes,
  anularCambio,
  BUCKET_TICKETS,
  guardar,
  historial as traerHistorial,
  pedirCambio,
  ticketsCombustible,
  TOPE_RANGO,
} from "../services/ordenGasoil";
import { diaHoraAr } from "../shared/fechaAr";
import {
  EMPRESAS_FACTURA,
  MOTIVOS,
  POR,
  YPFS,
  alertasTicket,
  armar,
  avisoBorrada,
  avisoCambioPedido,
  dniLindo,
  empresaDelTractor,
  filtrarPorQuien,
  limitesDelRango,
  litrosDelHistorial,
  litrosDelRanking,
  cambioPendiente,
  desdeParaTickets,
  estadoCambio,
  fotosDeLaOrden,
  litrosParaEditar,
  litrosPedidos,
  litrosValidos,
  mailto,
  menosElQueManda,
  nombreCorto,
  preguntaBorrar,
  rangoPorDefecto,
  rankingPedidos,
  resumenTicket,
  sinFotoDelChofer,
} from "../shared/ordenGasoil";

/* Orden de gas oil — ticket 431 (Lautaro Díaz, Satelital, 16/10/2026).
 *
 * El gemelo del «Generar correo» de los enganches de bitren: se elige el
 * chofer (trae sus datos y su tractor), la YPF, a quién se factura, los
 * litros y el motivo, y la pantalla arma el correo. Lo manda el que lo
 * genera, desde SU correo: «Abrir en mi correo» abre uno nuevo con el Para,
 * la copia y el asunto, y en el mismo clic copia el cuerpo con la tabla para
 * pegarlo (ticket 571). Cada orden generada queda en `ordenes_gasoil`.
 *
 * «Borrar» en cada renglón del historial (ticket 448, Lautaro, 19/10: dos
 * órdenes de prueba). Pregunta EN LA PÁGINA por esa orden —chofer, litros,
 * día y hora: el 19/10 hubo dos de FANLOO por 300 l— y la marca como
 * borrada (`borrar_orden_gasoil`): no se elimina, una orden de gas oil es
 * plata. Puede borrar cualquiera que entre a la pantalla.
 *
 * «Pedir cambio de litros» en cada renglón (Lautaro, 21/10: «recien les pase
 * ordenes a 2 choferes y luego me pidieron mas litros porque no llegaban. […]
 * en el historial queda los lts originales que yo pase»). Nico, 21/10: «Si dan
 * algo mal que puedan cambiarlo con mi autorización». Así que se PIDE: litros
 * y motivo (`pedir_cambio_litros_orden_gasoil`), y a Nico le llega un WhatsApp
 * con «Autorizar» y «Rechazar» (FlotaBot, `gasoil_cambios.py`). Mientras
 * espera, la celda lo dice y se puede retirar; autorizado, el renglón muestra
 * los litros nuevos y, abajo, lo pedido y quién autorizó. Cambia el registro,
 * no el correo: ése ya salió.
 *
 * «Foto del chofer» en cada renglón (Nico, 28/10, ticket 545: «cuando ellos
 * suben la foto, ¿no puede aparecer ahí junto a la orden? Por la fecha, el
 * chofer y el interno»): el ticket de carga que el chofer le mandó a FlotaBot
 * (`tickets_combustible`), con lo leído y un aviso si no parece un ticket o
 * si los litros no coinciden. El cruce, en `fotosDeLaOrden`.
 *
 * «Quién pide más» arriba del historial (Nico, 29/10, ticket 561: «filtrar
 * la info y saber quién es el que más pide órdenes de gas oil»): el
 * historial se mira entre dos fechas (los últimos 30 días al entrar), y el
 * ranking de esas fechas —por quien generó la orden o por chofer— cuenta
 * órdenes y litros. Tocar un nombre deja sólo las suyas.
 *
 * Sin textos de ayuda en la pantalla: el cómo está en /como/satelital.
 */

export const PERMISO = "cargar.satelital";

const titulo = (s) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase())
    .trim();

const etiquetaChofer = (c) =>
  [`${c.apellido || ""} ${c.nombres || ""}`.trim(), c.interno ? `INT ${c.interno}` : "", dniLindo(c.dni)]
    .filter(Boolean)
    .join(" · ");

async function copiarTexto(t) {
  try {
    await navigator.clipboard.writeText(t);
    return true;
  } catch {
    return false;
  }
}

/* La celda «Foto del chofer» (ticket 545): cada ticket que cruza con la
 * orden, el más cercano primero, con lo que se leyó y lo que hay que mirar. */
function FotoDelChofer({ orden, tickets }) {
  if (tickets.error) return <span className="tk-meta">{tickets.error}</span>;
  const fotos = fotosDeLaOrden(orden, tickets.filas);
  if (fotos.length === 0) return <span className="tk-meta">{sinFotoDelChofer(orden)}</span>;
  return fotos.map((t, i) => (
    <div key={t.id} data-testid="foto-chofer" style={i ? { marginTop: 6 } : undefined}>
      <FotoPrivada
        ruta={t.foto}
        bucket={BUCKET_TICKETS}
        etiqueta="ver foto"
        titulo={[t.chofer_nombre || orden.chofer, resumenTicket(t)].filter(Boolean).join(" · ")}
      />
      {resumenTicket(t) && (
        <>
          <br />
          <span className="tk-meta">{resumenTicket(t)}</span>
        </>
      )}
      {alertasTicket(orden, t).map((a) => (
        <span key={a}>
          <br />
          <span className="pill mal">{a}</span>
        </span>
      ))}
    </div>
  ));
}

/* El cuerpo con su formato: primero `text/html` por la API nueva; si el
 * navegador no la deja, se selecciona la vista previa y se copia como en
 * Enganches. */
async function copiarCorreo(c, nodo) {
  try {
    if (navigator.clipboard?.write && typeof window.ClipboardItem !== "undefined") {
      await navigator.clipboard.write([
        new window.ClipboardItem({
          "text/html": new Blob([c.html], { type: "text/html" }),
          "text/plain": new Blob([c.texto], { type: "text/plain" }),
        }),
      ]);
      return true;
    }
  } catch {
    /* cae a la selección */
  }
  try {
    if (!nodo || !document.execCommand) return copiarTexto(c.texto);
    const sel = window.getSelection();
    const rango = document.createRange();
    rango.selectNodeContents(nodo);
    sel.removeAllRanges();
    sel.addRange(rango);
    const ok = document.execCommand("copy");
    sel.removeAllRanges();
    return ok || copiarTexto(c.texto);
  } catch {
    return copiarTexto(c.texto);
  }
}

export default function OrdenGasoil() {
  const { permisos = null } = useOutletContext() || {};
  const { user, fullUser } = useAuth() || {};
  const puede = permisos === null || permisos.includes(PERMISO);

  const [lista, setLista] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [busca, setBusca] = useState("");
  const [chofer, setChofer] = useState(null);
  const [ypfId, setYpfId] = useState("");
  const [cuit, setCuit] = useState("");
  const [litros, setLitros] = useState("");
  const [tanque, setTanque] = useState(false);
  const [motivoId, setMotivoId] = useState("");
  const [correo, setCorreo] = useState(null);
  // Ticket 467: mientras se guarda, «Limpiar» espera; y si no se pudo
  // guardar, o si el correo armado todavía no se abrió ni se copió (`usado`),
  // pregunta antes de tirarlo.
  const [guardando, setGuardando] = useState(false);
  const [sinGuardar, setSinGuardar] = useState(false);
  const [usado, setUsado] = useState(false);
  const [copiaFallo, setCopiaFallo] = useState(false);
  const campoChofer = useRef(null);
  const [aviso, setAviso] = useState("");
  const [hist, setHist] = useState({ filas: [], error: "" });
  const [borrando, setBorrando] = useState(null);
  // «Pedir cambio de litros»: qué orden, cuántos litros y por qué.
  const [editando, setEditando] = useState(null);
  const [guardandoLitros, setGuardandoLitros] = useState(null);
  const [avisoHist, setAvisoHist] = useState("");
  const vista = useRef(null);

  // Los tickets de carga de los choferes, para «Foto del chofer» (ticket 545).
  const [tickets, setTickets] = useState({ filas: [], error: "" });

  // «Quién pide más» (ticket 561): las fechas que se miran, por quién se
  // cuenta y, si se tocó un nombre, de quién son las que quedan.
  const [rango, setRango] = useState(() => rangoPorDefecto());
  const [por, setPor] = useState("quien");
  const [filtro, setFiltro] = useState(null);
  const pedido = useRef(0);

  const recargarHistorial = useCallback(async () => {
    // Si se cambian las fechas rápido, vale la última consulta.
    const n = ++pedido.current;
    const h = await traerHistorial(TOPE_RANGO, limitesDelRango(rango));
    if (n !== pedido.current) return;
    setHist(h);
    const t = await ticketsCombustible(desdeParaTickets(h.filas));
    if (n === pedido.current) setTickets(t);
  }, [rango]);

  useEffect(() => {
    if (!puede) return undefined;
    let vivo = true;
    (async () => {
      const r = await traerChoferes();
      if (!vivo) return;
      setLista(r.filas);
      setError(r.error);
      setCargando(false);
    })();
    return () => {
      vivo = false;
    };
  }, [puede]);

  useEffect(() => {
    if (puede) recargarHistorial();
  }, [puede, recargarHistorial]);

  const ranking = useMemo(() => rankingPedidos(hist.filas, por), [hist.filas, por]);
  const filas = useMemo(() => filtrarPorQuien(hist.filas, por, filtro?.clave), [hist.filas, por, filtro]);

  const porEtiqueta = useMemo(() => new Map(lista.map((c) => [etiquetaChofer(c), c])), [lista]);

  const elegirChofer = (v) => {
    setBusca(v);
    const c = porEtiqueta.get(v) || null;
    setChofer(c);
    setCorreo(null);
    if (c) {
      const e = empresaDelTractor(c);
      if (e) setCuit(e.cuit);
    }
  };

  const ypf = YPFS.find((y) => y.id === ypfId) || null;
  const empresa = EMPRESAS_FACTURA.find((e) => e.cuit === cuit) || null;
  const motivo = MOTIVOS.find((m) => m.id === motivoId) || null;
  const lts = litrosValidos(litros);
  const listo = Boolean(chofer && ypf && empresa && (tanque || lts) && motivo);

  const firma = titulo([fullUser?.nombres, fullUser?.apellido].filter(Boolean).join(" "));
  // Ticket 467: el que manda —el que está en el hub— no va en la copia.
  const aMandar = correo && { ...correo, copia: menosElQueManda(correo.copia, [user?.email, fullUser?.mail]) };

  const generar = async () => {
    if (!listo) return;
    const c = armar({ chofer, ypf, empresa, litros: lts, tanqueCompleto: tanque, motivo, firma });
    setCorreo(c);
    setAviso("");
    setGuardando(true);
    setSinGuardar(false);
    setUsado(false);
    setCopiaFallo(false);
    const r = await guardar({
      usuario_nombre: firma || null,
      dni: chofer.dni,
      chofer: nombreCorto(chofer.apellido, chofer.nombres),
      interno: chofer.interno || null,
      dominio: chofer.dominio || null,
      ypf: ypf.nombre,
      empresa_cuit: empresa.cuit,
      empresa_nombre: empresa.nombre,
      // Con litros, la fila va como siempre y anda sin la migración del 449.
      ...(tanque ? { tanque_completo: true, litros: null } : { litros: lts }),
      motivo: motivo.nombre,
      asunto: c.asunto,
    });
    if (!r.ok) setAviso(r.error || "No se pudo guardar en el historial.");
    recargarHistorial();
    setGuardando(false);
    setSinGuardar(!r.ok);
  };

  const borrarOrden = async (f) => {
    setBorrando(f.id);
    setAvisoHist("");
    const r = await borrar(f.id);
    setBorrando(null);
    if (!r.ok) {
      setAvisoHist(r.error);
      return;
    }
    setHist((h) => ({ ...h, filas: h.filas.filter((x) => x.id !== f.id) }));
    setAvisoHist(avisoBorrada(f));
  };

  const pedirLitros = async (f) => {
    const n = litrosValidos(editando?.valor);
    const motivoCambio = (editando?.motivo || "").trim();
    if (!n || !motivoCambio) return;
    setGuardandoLitros(f.id);
    setAvisoHist("");
    const r = await pedirCambio(f.id, n, motivoCambio, firma);
    setGuardandoLitros(null);
    if (!r.ok) {
      setAvisoHist(r.error);
      return;
    }
    setHist((h) => ({ ...h, filas: h.filas.map((x) => (x.id === f.id ? { ...x, cambio: r.cambio } : x)) }));
    setEditando(null);
    setAvisoHist(avisoCambioPedido(f, n));
  };

  const retirarCambio = async (f) => {
    const c = cambioPendiente(f);
    if (!c) return;
    setGuardandoLitros(f.id);
    setAvisoHist("");
    const r = await anularCambio(c.id);
    setGuardandoLitros(null);
    if (!r.ok) {
      setAvisoHist(r.error);
      return;
    }
    setHist((h) => ({ ...h, filas: h.filas.map((x) => (x.id === f.id ? { ...x, cambio: r.cambio } : x)) }));
  };

  const copiar = async (que, fn) => setAviso((await fn()) ? `${que}: copiado.` : "No se pudo copiar.");

  /* «Limpiar» (ticket 467, Lautaro, 20/10: «para que cuando yo cargue y quiera
   * hacer una nueva orden se limpien los datos»): la pantalla como al entrar
   * —chofer y lo que trae de su tractor, YPF, a quién se factura, litros,
   * motivo y el correo armado— y el cursor en el chofer. El historial no se
   * toca: la orden generada ya quedó guardada al generarla. */
  const limpiar = () => {
    setBusca("");
    setChofer(null);
    setYpfId("");
    setCuit("");
    setLitros("");
    setTanque(false);
    setMotivoId("");
    setCorreo(null);
    setAviso("");
    setSinGuardar(false);
    campoChofer.current?.focus();
  };

  /* Lo que pregunta «Limpiar» antes de tirar el correo armado: si no quedó en
   * el historial, o si todavía no se abrió ni se copió (tirarlo antes de
   * mandarlo obliga a generarlo otra vez, y eso deja otra orden en el
   * historial; revisión del ticket 467). Abierto o copiado, no pregunta. */
  const preguntaLimpiar = () => {
    if (!correo) return "";
    if (sinGuardar) return "Este correo no quedó en el historial. ¿Limpiar igual?";
    if (!usado) return "Todavía no abriste ni copiaste este correo. ¿Limpiar igual?";
    return "";
  };

  const copiarElCorreo = async () => {
    const ok = await copiarCorreo(correo, vista.current);
    if (ok) setUsado(true);
    setCopiaFallo(!ok);
    return ok;
  };

  /* Un solo botón (ticket 571, Lautaro, 30/10: «en vez de haber un boton de
   * copiar correo y abrir, no se puede poner uno solo de los dos?»): abre el
   * correo nuevo con Para, copia y asunto, y en el mismo clic deja el cuerpo
   * copiado con la tabla. Pegarlo solo no se puede: un `mailto:` lleva texto
   * plano, y la tabla se perdería. Si la copia falla, aparece «Copiar el
   * correo» para reintentar. El link sigue su `href`: no se frena. */
  const abrirYCopiar = () => {
    setUsado(true);
    // `copiarCorreo` y no `copiarElCorreo`: el abrir ya marcó usado, y una
    // copia que termina tarde no puede marcar el correo que se generó después.
    copiarCorreo(correo, vista.current).then((ok) => {
      setCopiaFallo(!ok);
      setAviso(
        ok
          ? "El cuerpo ya está copiado: en el correo que se abrió, hacé clic en el cuerpo y pegá (Ctrl+V)."
          : "Se abrió el correo, pero no se pudo copiar el cuerpo. Probá con «Copiar el correo».",
      );
    });
  };

  if (!puede) return <PuertaCerrada />;

  return (
    <div>
      <h2 style={{ margin: "0 0 12px" }}>Orden de gas oil</h2>

      {error && (
        <div className="panel-aviso suave">
          <p>{error}</p>
        </div>
      )}

      <div style={{ display: "grid", gap: 10, maxWidth: 560 }}>
        <label className="tk-campo">
          <span>Chofer</span>
          <input
            ref={campoChofer}
            list="orden-gasoil-choferes"
            value={busca}
            onChange={(e) => elegirChofer(e.target.value)}
            placeholder={cargando ? "Cargando…" : "Apellido, interno o DNI"}
            aria-label="Chofer"
          />
          <datalist id="orden-gasoil-choferes">
            {lista.map((c) => (
              <option key={c.dni} value={etiquetaChofer(c)} />
            ))}
          </datalist>
        </label>

        {chofer && (
          <p className="tk-meta" data-testid="datos-chofer">
            {[
              chofer.interno ? `INT ${chofer.interno}` : "Sin tractor asignado",
              chofer.dominio,
              chofer.marca,
              chofer.anio,
              dniLindo(chofer.dni),
              chofer.telefono,
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}

        <label className="tk-campo">
          <span>YPF</span>
          <select value={ypfId} onChange={(e) => { setYpfId(e.target.value); setCorreo(null); }} aria-label="YPF">
            <option value="">—</option>
            {YPFS.map((y) => (
              <option key={y.id} value={y.id}>{y.nombre}</option>
            ))}
          </select>
        </label>

        <label className="tk-campo">
          <span>Facturar a</span>
          <select value={cuit} onChange={(e) => { setCuit(e.target.value); setCorreo(null); }} aria-label="Facturar a">
            <option value="">—</option>
            {EMPRESAS_FACTURA.map((e) => (
              <option key={e.cuit} value={e.cuit}>{`${e.sigla} — ${e.nombre}`}</option>
            ))}
          </select>
        </label>

        <label className="tk-campo">
          <span>Litros</span>
          <input
            type="number"
            min="1"
            max="2000"
            step="1"
            value={litros}
            onChange={(e) => { setLitros(e.target.value); setCorreo(null); }}
            placeholder={tanque ? "" : "300"}
            aria-label="Litros"
            disabled={tanque}
          />
        </label>

        {/* Ticket 449 (Lautaro, 19/10): «hay veces en las que los choferes
            completan los tanques». Tildado, los litros se limpian y no se
            piden; el correo dice TANQUE COMPLETO donde iban. */}
        <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="checkbox"
            checked={tanque}
            onChange={(e) => { setTanque(e.target.checked); setLitros(""); setCorreo(null); }}
          />
          Tanque completo
        </label>

        <label className="tk-campo">
          <span>Motivo</span>
          <select value={motivoId} onChange={(e) => { setMotivoId(e.target.value); setCorreo(null); }} aria-label="Motivo">
            <option value="">—</option>
            {MOTIVOS.map((m) => (
              <option key={m.id} value={m.id}>{m.nombre}</option>
            ))}
          </select>
        </label>

        <p>
          <button type="button" className="tk-boton tk-boton--fuerte" onClick={generar} disabled={!listo}>
            Generar correo
          </button>{" "}
          {preguntaLimpiar() ? (
            <BotonConfirmar
              className="tk-boton"
              pregunta={preguntaLimpiar()}
              si="Sí, limpiar"
              onConfirmar={limpiar}
              disabled={guardando}
            >
              Limpiar
            </BotonConfirmar>
          ) : (
            <button type="button" className="tk-boton" onClick={limpiar} disabled={guardando}>
              Limpiar
            </button>
          )}
        </p>
      </div>

      {correo && (
        <div className="panel-aviso suave" style={{ marginTop: 12 }}>
          <p className="tk-meta">
            <b>Para:</b> {correo.para.join(", ")}
            <br />
            <b>Copia:</b> {aMandar.copia.join(", ")}
            <br />
            <b>Asunto:</b> {correo.asunto}
          </p>
          {correo.faltan.length > 0 && <p className="tk-meta">Vacío: {correo.faltan.map((f) => f.replace(/\s+/g, " ")).join(", ")}</p>}
          <div className="tk-acciones" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <a className="tk-boton tk-boton--fuerte" href={mailto(aMandar)} onClick={abrirYCopiar}>
              Abrir en mi correo
            </a>
            {copiaFallo && (
              <button type="button" className="tk-boton" onClick={() => copiar("El correo", copiarElCorreo)}>
                Copiar el correo
              </button>
            )}
          </div>
          {aviso && <p className="tk-meta">{aviso}</p>}
          <div
            ref={vista}
            data-testid="vista-correo"
            style={{ background: "#fff", color: "#000", padding: 12, marginTop: 8, overflowX: "auto" }}
            dangerouslySetInnerHTML={{ __html: correo.html }}
          />
        </div>
      )}

      <h3 style={{ marginTop: 24 }}>Historial</h3>
      {avisoHist && (
        <p className="tk-meta" role="status">
          {avisoHist}
        </p>
      )}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "flex-end", marginBottom: 10 }}>
        <label className="tk-campo">
          <span>Desde</span>
          <input
            type="date"
            value={rango.desde}
            max={rango.hasta || undefined}
            onChange={(e) => setRango((r) => ({ ...r, desde: e.target.value }))}
            aria-label="Desde"
          />
        </label>
        <label className="tk-campo">
          <span>Hasta</span>
          <input
            type="date"
            value={rango.hasta}
            min={rango.desde || undefined}
            onChange={(e) => setRango((r) => ({ ...r, hasta: e.target.value }))}
            aria-label="Hasta"
          />
        </label>
        <label className="tk-campo">
          <span>Quién pide más, por</span>
          <select
            value={por}
            onChange={(e) => {
              setPor(e.target.value);
              setFiltro(null);
            }}
            aria-label="Quién pide más, por"
          >
            {POR.map((p) => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
        </label>
      </div>

      {!hist.error && ranking.length > 0 && (
        <table className="tabla" aria-label="Quién pide más" style={{ width: "auto", marginBottom: 12 }}>
          <thead>
            <tr>
              <th>{por === "chofer" ? "Chofer" : "Quién"}</th>
              <th>Órdenes</th>
              <th>Litros</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((g) => (
              <tr key={g.clave}>
                <td>
                  <button
                    type="button"
                    className="link-button"
                    aria-pressed={filtro?.clave === g.clave}
                    onClick={() => setFiltro(filtro?.clave === g.clave ? null : { clave: g.clave, nombre: g.nombre })}
                    style={filtro?.clave === g.clave ? { fontWeight: "bold" } : undefined}
                  >
                    {g.nombre}
                  </button>
                </td>
                <td>{g.ordenes}</td>
                <td>{litrosDelRanking(g)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {filtro && (
        <p className="tk-meta" data-testid="filtro-quien">
          Sólo las de <b>{filtro.nombre}</b>: {filas.length} {filas.length === 1 ? "orden" : "órdenes"}.{" "}
          <button type="button" className="link-button" onClick={() => setFiltro(null)}>
            Ver todas
          </button>
        </p>
      )}

      {hist.error ? (
        <p className="tk-meta">{hist.error}</p>
      ) : filas.length === 0 ? (
        <p className="tk-vacio">No hay órdenes entre esas fechas.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table className="tabla">
            <thead>
              <tr>
                <th>Cuándo</th>
                <th>Chofer</th>
                <th>INT</th>
                <th>YPF</th>
                <th>Litros</th>
                <th>Foto del chofer</th>
                <th>Motivo</th>
                <th>Factura</th>
                <th>Quién</th>
                <th aria-label="Cambiar o borrar" />
              </tr>
            </thead>
            <tbody>
              {filas.map((f) => (
                <tr key={f.id}>
                  <td>{diaHoraAr(f.creado_en)}</td>
                  <td>{f.chofer}</td>
                  <td>{f.interno || ""}</td>
                  <td>{f.ypf}</td>
                  <td>
                    {litrosDelHistorial(f)}
                    {litrosPedidos(f) && (
                      <>
                        <br />
                        <span className="tk-meta">{litrosPedidos(f)}</span>
                      </>
                    )}
                    {estadoCambio(f) && (
                      <>
                        <br />
                        <span className="tk-meta">{estadoCambio(f)}</span>
                      </>
                    )}
                  </td>
                  <td>
                    <FotoDelChofer orden={f} tickets={tickets} />
                  </td>
                  <td>{f.motivo}</td>
                  <td>{f.empresa_nombre}</td>
                  <td>{f.usuario_nombre || f.usuario}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {editando?.id === f.id ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <input
                          type="number"
                          min="1"
                          max="2000"
                          step="1"
                          value={editando.valor}
                          onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
                          aria-label="Litros cargados"
                          placeholder="Litros"
                          style={{ width: 80 }}
                          autoFocus
                        />
                        <input
                          type="text"
                          maxLength={300}
                          value={editando.motivo}
                          onChange={(e) => setEditando({ ...editando, motivo: e.target.value })}
                          aria-label="Motivo del cambio"
                          placeholder="Motivo"
                          style={{ width: 180 }}
                        />
                        <button
                          type="button"
                          className="tk-boton tk-boton--chico"
                          onClick={() => pedirLitros(f)}
                          disabled={
                            !litrosValidos(editando.valor) || !editando.motivo.trim() || guardandoLitros !== null
                          }
                        >
                          {guardandoLitros === f.id ? "Pidiendo…" : "Pedir"}
                        </button>
                        <button
                          type="button"
                          className="tk-boton tk-boton--chico tk-boton--flojo"
                          onClick={() => setEditando(null)}
                          disabled={guardandoLitros !== null}
                        >
                          Cancelar
                        </button>
                      </span>
                    ) : (
                      <>
                        {cambioPendiente(f) ? (
                          <button
                            type="button"
                            className="tk-boton tk-boton--chico tk-boton--flojo"
                            onClick={() => retirarCambio(f)}
                            disabled={borrando !== null || guardandoLitros !== null}
                          >
                            {guardandoLitros === f.id ? "Retirando…" : "Retirar pedido"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="tk-boton tk-boton--chico tk-boton--flojo"
                            onClick={() => {
                              setAvisoHist("");
                              setEditando({ id: f.id, valor: litrosParaEditar(f), motivo: "" });
                            }}
                            disabled={borrando !== null || guardandoLitros !== null}
                          >
                            Pedir cambio de litros
                          </button>
                        )}{" "}
                        <BotonConfirmar
                          className="tk-boton tk-boton--chico tk-boton--flojo"
                          claseSi="tk-boton tk-boton--chico"
                          disabled={borrando !== null || guardandoLitros !== null}
                          pregunta={preguntaBorrar(f)}
                          si="Sí, borrar"
                          onConfirmar={() => borrarOrden(f)}
                        >
                          {borrando === f.id ? "Borrando…" : "Borrar"}
                        </BotonConfirmar>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
